import { useState } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/data/site";

const ROWS = [
  { label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-row-email" },
  {
    label: "Phone",
    value: CONTACT.phone,
    href: `tel:${CONTACT.phone.replace(/\s/g, "")}`,
    testid: "contact-row-phone",
  },
];

const inputClass =
  "w-full bg-transparent border-b border-white/15 focus:border-white/60 outline-none py-3.5 text-sm text-neutral-100 placeholder:text-neutral-500 transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setForm({ name: "", company: "", email: "", message: "" });
      toast.success("Enquiry sent — thank you. We'll get back to you within 24 hours.");
    } catch {
      toast.error("Something went wrong sending your enquiry — please try again, or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-40 px-5 sm:px-8 border-t border-white/5">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <Reveal>
            <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-400 mb-5">Contact</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-neutral-50">
              Let's build something great.
            </h2>
            <p className="text-neutral-400 mt-6 max-w-md leading-relaxed text-sm sm:text-base">
              Tell us about your business and what you want your website to do. We reply personally — no agencies,
              no middlemen.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            {ROWS.map((r) => (
              <a
                key={r.label}
                data-testid={r.testid}
                href={r.href}
                target={r.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="group flex items-center justify-between border-b border-white/10 py-5 first:border-t"
              >
                <span className="mono text-[10px] tracking-[0.25em] uppercase text-neutral-400">{r.label}</span>
                <span className="inline-flex items-center gap-2 text-sm text-neutral-100 group-hover:text-white transition-colors duration-300">
                  {r.value}
                  <ArrowUpRight
                    size={14}
                    className="text-neutral-500 group-hover:text-neutral-200 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </span>
              </a>
            ))}
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-8 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <label htmlFor="contact-name" className="mono text-[10px] tracking-[0.25em] uppercase text-neutral-400 block mb-1">
                  Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-company" className="mono text-[10px] tracking-[0.25em] uppercase text-neutral-400 block mb-1">
                  Business / Company
                </label>
                <input
                  id="contact-company"
                  data-testid="contact-company-input"
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Your business"
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-email" className="mono text-[10px] tracking-[0.25em] uppercase text-neutral-400 block mb-1">
                  Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@company.com"
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="mono text-[10px] tracking-[0.25em] uppercase text-neutral-400 block mb-1">
                  Tell us about your project
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="What does your business do, and what should your new website achieve?"
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-full bg-neutral-50 text-[#0a0a0b] font-medium pl-8 pr-7 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Enquiry"}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
